import { useEffect, Dispatch, useState } from 'react';
import { RequestAction, requestIdFromBase64 } from '.';
import {
  IError,
  ISaveRequestOutput,
  useUserContext,
  ResponseCSOSHeaders,
  useQuery,
  IRequestEnrollmentVerificationInput,
  requestEnrollmentVerification,
} from '../common';
import { useSubmitRequestFlow } from '../login';
import consentjson from '../consent.json';
import { isEmpty } from 'lodash/fp';
import { pathOr } from 'rambda';
import { fold, left, map as emap, right } from 'fp-ts/lib/Either';
import { flow } from 'fp-ts/function';

export function useEmploymentVerification({
  dispatchRequest,
}: {
  dispatchRequest: Dispatch<RequestAction>;
}) {
  const { user } = useUserContext();

  const [requestId, setRequestId] = useState(0);
  const state = useQuery().get('state');

  useEffect(() => {
    flow(requestIdFromBase64, emap(setRequestId))(state);
  }, [state]);

  const submitRequestFlow = useSubmitRequestFlow();

  const [code, setCode] = useState('');
  const query = useQuery();

  useEffect(() => {
    flow(
      query.get.bind(query),
      code =>
        code !== null && submitRequestFlow ? right(code as string) : left(''),
      emap(setCode)
    )('code');
  }, [submitRequestFlow, query]);

  useEffect(() => {
    flow(
      getRequest,
      emap<IRequestEnrollmentVerificationInput, void>(
        handleRequest(
          pathOr(
            {
              'content-type': '',
              authorization: '',
              'x-csrf-csos-token': '',
            },
            'icam.headers'
          )(user)
        )(dispatchRequest)
      )
    )({
      requestId,
      loginGovCode: code,
      agreeToTerms: true,
      statement: consentjson.employmentVerificationConsent,
    });
  }, [requestId, code, dispatchRequest, user]);

  return { submitRequestFlow };
}

const getRequest = (input: IRequestEnrollmentVerificationInput) =>
  input.requestId > 0 && !isEmpty(input.loginGovCode)
    ? right(input)
    : left(input);

const handleRequest =
  (hR: ResponseCSOSHeaders) =>
  (dispatchRequest: Dispatch<RequestAction>) =>
  (request: IRequestEnrollmentVerificationInput) => {
    dispatchRequest({ type: 'ConfirmSubmitRequest', payload: true });

    requestEnrollmentVerification(hR)(request).then(
      fold<IError, ISaveRequestOutput, void>(
        ({ code, message }) =>
          dispatchRequest({
            type: 'SaveRequestError',
            payload: { code, message },
          }),
        b => {
          dispatchRequest({
            type: 'SaveRequestSuccess',
            payload: b,
          });
        }
      )
    );
  };
